import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientsQueryOptions } from '@/features/clients';
import {
  PassModal,
  passDraftToUpdateRequest,
  useDeletePass,
  useUpdatePass,
} from '@/features/passes';
import { LoadingFallback, QueryErrorPlaceholder } from '@/shared/ui';
import type { Pass } from '@/types';

interface QuickPassEditorProps {
  isOpen: boolean;
  clientId: string;
  passId: string;
  onClose: () => void;
}

export function QuickPassEditor({ isOpen, clientId, passId, onClose }: QuickPassEditorProps) {
  const detail = useQuery({
    ...clientsQueryOptions.detail(clientId),
    enabled: isOpen,
  });
  // The list contains only a summary. Capture the complete, freshly loaded pass
  // once so mutation invalidation cannot reset a form the user is editing.
  const [editingPass, setEditingPass] = useState<Pass>();
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    if (isOpen) setEditingPass(undefined);
  }
  if (isOpen && !editingPass && detail.isSuccess && !detail.isFetching) {
    const pass = detail.data.passes.find((item) => item.id === passId);
    if (pass) setEditingPass(pass);
  }

  const updatePass = useUpdatePass({ onSuccess: onClose });
  const deletePass = useDeletePass();

  const loading = detail.isPending || detail.isFetching;
  return (
    <PassModal
      isOpen={isOpen}
      mode="edit"
      placeholder={
        !editingPass ? (
          loading ? (
            <LoadingFallback />
          ) : (
            <QueryErrorPlaceholder
              header={detail.isError ? 'Could not load pass' : 'This pass is no longer available'}
              error={detail.error}
              onRetry={() => void detail.refetch()}
            />
          )
        ) : undefined
      }
      editPass={editingPass}
      onClose={onClose}
      isLoading={updatePass.isPending || deletePass.isPending}
      onSave={async (draft) => {
        await updatePass.mutateAsync({
          id: passId,
          clientId,
          data: passDraftToUpdateRequest(draft),
        });
      }}
      onDelete={async (id) => {
        await deletePass.mutateAsync({ id, clientId });
      }}
    />
  );
}
